// src/components/modals/AllReportsModal.jsx
import { useState } from 'react'
import { X, FileText, Calendar, TrendingUp, RotateCcw, Package, AlertTriangle, ClipboardList } from 'lucide-react'
import ReportService from '../../services/reportService'

const AllReportsModal = ({ isOpen, onClose, branchId: propBranchId, branchInfo: propBranchInfo }) => {
  const [loading, setLoading] = useState(false)
  const [selectedReport, setSelectedReport] = useState(null)

  if (!isOpen) return null

  // ─────────────────────────────────────────────
  // Branch resolution — prop → localStorage fallback
  // This ensures cashiers and admins using POS always
  // get their own branch data, never unfiltered data.
  // ─────────────────────────────────────────────
  let resolvedBranchId = propBranchId
  let resolvedBranchInfo = propBranchInfo

  if (!resolvedBranchId) {
    try {
      // Try the selected branch stored in POS session
      const storedBranch = localStorage.getItem('tezlaa-selected-branch')
      if (storedBranch) {
        const parsed = JSON.parse(storedBranch)
        resolvedBranchId = parsed._id || parsed.id
        resolvedBranchInfo = resolvedBranchInfo || parsed
      }
    } catch { /* ignore parse errors */ }
  }

  if (!resolvedBranchId) {
    try {
      // Fall back to the user's own branchId from auth data
      const storedUser = localStorage.getItem('tezlaa-user')
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser)
        resolvedBranchId = parsedUser.branchId
        if (!resolvedBranchInfo && parsedUser.branch) {
          resolvedBranchInfo = parsedUser.branch
        }
      }
    } catch { /* ignore parse errors */ }
  }

  const branchId = resolvedBranchId
  const branchInfo = resolvedBranchInfo

  // ─────────────────────────────────────────────
  // Shared CSS — matches Courier Prime receipt style
  // ─────────────────────────────────────────────
  const getReceiptCSS = () => `
    @import url('https://fonts.googleapis.com/css2?family=Courier+Prime:wght@400;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Courier Prime', 'Courier New', monospace;
      width: 302px;
      max-width: 302px;
      margin: 0 auto;
      padding: 12px 10px;
      background: #fff;
      color: #000;
      font-size: 13px;
      line-height: 1.55;
    }
    .header {
      text-align: center;
      padding-bottom: 10px;
      border-bottom: 2px dashed #000;
      margin-bottom: 10px;
    }
    .company-name {
      font-size: 16px;
      font-weight: 700;
      letter-spacing: 2px;
      text-transform: uppercase;
      margin-bottom: 3px;
    }
    .company-info { font-size: 12px; line-height: 1.6; }
    .report-title {
      font-size: 14px;
      font-weight: 700;
      margin-top: 8px;
      letter-spacing: 3px;
      text-align: center;
    }
    .section { margin-bottom: 8px; }
    hr.sep {
      border: none;
      border-top: 1px dashed #000;
      margin: 8px 0;
    }
    hr.sep-solid {
      border: none;
      border-top: 1px solid #000;
      margin: 6px 0;
    }
    .info-row {
      display: flex;
      justify-content: space-between;
      font-size: 12px;
      margin-bottom: 2px;
    }
    .info-label { font-weight: 600; white-space: nowrap; margin-right: 4px; }
    .info-value { text-align: right; }
    .items-header {
      display: flex;
      font-weight: 700;
      font-size: 12px;
      padding: 3px 0;
      border-bottom: 1px solid #000;
      margin-bottom: 4px;
    }
    .col-order { flex: 2; }
    .col-type  { flex: 1; text-align: center; }
    .col-disc  { flex: 1; text-align: right; }
    .col-amt   { flex: 1; text-align: right; }
    .item-row {
      display: flex;
      font-size: 12px;
      margin-bottom: 3px;
      align-items: flex-start;
    }
    .item-row.return-row { color: #000; }
    .item-row.return-row .col-amt { font-weight: 700; }
    .total-row {
      display: flex;
      justify-content: space-between;
      font-size: 13px;
      margin-bottom: 3px;
    }
    .total-row.final {
      font-size: 14px;
      font-weight: 700;
      padding-top: 5px;
      border-top: 2px solid #000;
      margin-top: 4px;
    }
    .total-row.returns { }
    .summary-row {
      display: flex;
      justify-content: space-between;
      font-size: 12px;
      margin-bottom: 2px;
    }
    .summary-row.bold { font-weight: 700; }
    .footer {
      text-align: center;
      padding-top: 10px;
      font-size: 11px;
      color: #000;
    }
    .footer .thank-you {
      font-size: 13px;
      font-weight: 700;
      margin-bottom: 3px;
    }
    @media print {
      body { width: 100%; max-width: none; padding: 5px; }
      @page { size: 80mm auto; margin: 0; }
    }
  `

  // ─────────────────────────────────────────────
  // Shared header block
  // ─────────────────────────────────────────────
  const headerBlock = (title) => {
    const d = new Date()
    const date = d.toLocaleDateString('en-GB')
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    return `
      <div class="header">
        <div class="company-name">${branchInfo?.name || 'TEZLAA CAFÉ'}</div>
        ${branchInfo?.address ? `<div class="company-info">${branchInfo.address}</div>` : ''}
        ${branchInfo?.phoneNumber ? `<div class="company-info">Tel: ${branchInfo.phoneNumber}</div>` : ''}
        <div class="report-title">*** ${title} ***</div>
      </div>
      <div class="section">
        <div class="info-row"><span class="info-label">Date:</span><span class="info-value">${date}</span></div>
        <div class="info-row"><span class="info-label">Time:</span><span class="info-value">${time}</span></div>
        <div class="info-row"><span class="info-label">Branch:</span><span class="info-value">${branchInfo?.name || 'N/A'}</span></div>
      </div>
    `
  }

  // ─────────────────────────────────────────────
  // Shared footer block
  // ─────────────────────────────────────────────
  const footerBlock = () => `
    <hr class="sep"/>
    <div class="footer">
      <div class="thank-you">- END OF REPORT -</div>
      <div>Printed: ${new Date().toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
    </div>
  `

  const fmt = (n) => `Rs.${Math.abs(n).toFixed(2)}`

  // ─────────────────────────────────────────────
  // SALES REPORT HTML
  // ─────────────────────────────────────────────
  const generateSalesReportHTML = (salesData) => {
    const { orders, totals } = salesData.data

    const rows = orders.map(o => {
      const amtStr = o.isReturn ? `(${fmt(o.amount)})` : fmt(o.amount)
      const discStr = o.discount > 0 ? fmt(o.discount) : '-'
      const rowClass = o.isReturn ? 'item-row return-row' : 'item-row'
      const typeShort = o.isReturn ? 'RETURN' : (o.orderType?.toUpperCase().slice(0, 5) || 'SALE')
      return `
        <div class="${rowClass}">
          <span class="col-order">${o.orderNumber}</span>
          <span class="col-type">${typeShort}</span>
          <span class="col-disc">${discStr}</span>
          <span class="col-amt">${o.isReturn ? '(' + fmt(o.amount) + ')' : fmt(o.amount)}</span>
        </div>`
    }).join('')

    const sc  = totals.netServiceCharge ?? totals.totalServiceCharge ?? 0
    const rsc = totals.returnedServiceCharge || 0
    const gsc = totals.totalServiceCharge || 0
    const gt  = totals.grandTotal ?? totals.netSales

    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Sales Report</title><style>${getReceiptCSS()}</style></head><body>
      ${headerBlock('SALES REPORT')}
      <div class="section">
        <div class="items-header">
          <span class="col-order">Order #</span>
          <span class="col-type">Type</span>
          <span class="col-disc">Disc.</span>
          <span class="col-amt">Amount</span>
        </div>
        ${rows || '<div style="text-align:center;font-size:12px;padding:8px 0;">No sales today</div>'}
      </div>
      <hr class="sep-solid"/>
      <div class="section">
        <div class="total-row"><span>Gross Sales:</span><span>${fmt(totals.totalSales)}</span></div>
        ${totals.totalDiscounts > 0 ? `<div class="total-row"><span>Total Discounts:</span><span>- ${fmt(totals.totalDiscounts)}</span></div>` : ''}
        ${totals.totalReturns > 0 ? `<div class="total-row returns"><span>Total Returns:</span><span>(${fmt(totals.totalReturns)})</span></div>` : ''}
        <div class="total-row" style="border-top:1px dashed #000;padding-top:4px;margin-top:4px;"><span>Net Sales:</span><span>${fmt(totals.netSales)}</span></div>
        ${gsc > 0 ? `<div class="total-row"><span>Service Charge (Gross):</span><span>+ ${fmt(gsc)}</span></div>` : ''}
        ${rsc > 0 ? `<div class="total-row"><span>SC on Returns:</span><span>(${fmt(rsc)})</span></div>` : ''}
        ${sc > 0 ? `<div class="total-row"><span>Net Service Charge:</span><span>+ ${fmt(sc)}</span></div>` : ''}
        <div class="total-row final"><span>GRAND TOTAL:</span><span>${fmt(gt)}</span></div>
      </div>
      ${footerBlock()}
    </body></html>`
  }

  // ─────────────────────────────────────────────
  // RETURNS REPORT HTML
  // ─────────────────────────────────────────────
  const generateReturnsReportHTML = (returnsData) => {
    const { returns, totals } = returnsData.data

    const rows = returns.map(r => {
      const itemList = r.items?.map(i => `${i.product?.name || 'Item'} x${i.returnQuantity || 1}`).join(', ') || ''
      return `
        <div class="item-row" style="flex-direction:column; margin-bottom:8px; border-bottom:1px dashed #ccc; padding-bottom:6px;">
          <div style="display:flex; justify-content:space-between; font-weight:700; font-size:13px;">
            <span>${r.orderNumber}</span>
            <span>(${fmt(r.returnAmount)})</span>
          </div>
          <div style="font-size:11px; margin-top:2px;">
            <span>Customer: ${r.customerName}</span>
          </div>
          ${itemList ? `<div style="font-size:11px;">Items: ${itemList}</div>` : ''}
          <div style="font-size:11px; display:flex; justify-content:space-between;">
            <span>Reason: ${(r.reasonType || '').replace(/_/g, ' ')}</span>
            <span>Refund: ${r.refundMethod}</span>
          </div>
          <div style="font-size:11px;">Handled by: ${r.handledBy}</div>
        </div>`
    }).join('')

    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Returns Report</title><style>${getReceiptCSS()}</style></head><body>
      ${headerBlock('RETURNS REPORT')}
      <div class="section">
        ${rows || '<div style="text-align:center;font-size:12px;padding:8px 0;">No returns today</div>'}
      </div>
      <hr class="sep-solid"/>
      <div class="section">
        <div class="summary-row"><span>Total Returns:</span><span>${returns.length} orders</span></div>
        <div class="summary-row"><span>Total Items Returned:</span><span>${totals.totalItems}</span></div>
        <div class="total-row final"><span>TOTAL REFUNDED:</span><span>(${fmt(totals.totalReturns)})</span></div>
      </div>
      ${footerBlock()}
    </body></html>`
  }

  // ─────────────────────────────────────────────
  // DAILY AUDIT SUMMARY HTML
  // Matches the thermal receipt format: Daily Sales Summary → Payment Wise → Charges → Products Out
  // ─────────────────────────────────────────────
  const generateDailySummaryReportHTML = (reportData) => {
    const { summary, paymentBreakdown, products } = reportData.data
    const s = summary

    const paymentRows = Object.entries(paymentBreakdown || {})
      .map(([method, amount]) => `
        <div class="summary-row">
          <span>${method}:</span>
          <span>${fmt(amount)}</span>
        </div>`).join('')

    const productRows = (products || []).map(p => `
      <div class="item-row" style="margin-bottom:3px;">
        <span style="flex:4; font-size:12px; word-break:break-word; padding-right:4px;">${p.name}</span>
        <span style="flex:1; font-size:12px; text-align:right; white-space:nowrap;">${parseFloat(p.qty).toLocaleString('en-US', {maximumFractionDigits:3})}</span>
        <span style="flex:2; font-size:12px; text-align:right; white-space:nowrap;">${fmt(p.revenue)}</span>
      </div>`).join('')

    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Daily Audit Summary</title><style>${getReceiptCSS()}</style></head><body>
      ${headerBlock('DAILY AUDIT SUMMARY')}

      <hr class="sep"/>
      <div style="text-align:center;font-size:13px;font-weight:700;letter-spacing:2px;margin-bottom:6px;">DAILY SALES SUMMARY</div>
      <hr class="sep"/>
      <div class="section">
        <div class="summary-row"><span>Total Orders:</span><span>${s.totalOrders}</span></div>
        <div class="summary-row"><span>Gross Sales:</span><span>${fmt(s.grossSales)}</span></div>
        ${s.totalDiscounts > 0 ? `<div class="summary-row"><span>Discounts:</span><span>- ${fmt(s.totalDiscounts)}</span></div>` : `<div class="summary-row"><span>Discounts:</span><span>0.00</span></div>`}
        ${s.totalRefunds > 0 ? `<div class="summary-row"><span>Refunds (${s.totalReturns}):</span><span>(${fmt(s.totalRefunds)})</span></div>` : `<div class="summary-row"><span>Refunds:</span><span>0.00</span></div>`}
        <hr class="sep"/>
        <div class="summary-row bold"><span>Net Sales:</span><span>${fmt(s.netSales)}</span></div>
        <div class="summary-row"><span>Total Tendered:</span><span>${fmt(s.grandTotal)}</span></div>
      </div>

      <hr class="sep"/>
      <div style="text-align:center;font-size:13px;font-weight:700;letter-spacing:2px;margin-bottom:6px;">PAYMENT TYPE WISE SALE</div>
      <hr class="sep"/>
      <div class="section">
        ${paymentRows || '<div style="text-align:center;font-size:12px;">No payment data</div>'}
      </div>

      ${(s.totalServiceCharge > 0 || s.totalPackageFee > 0) ? `
      <hr class="sep"/>
      <div style="text-align:center;font-size:13px;font-weight:700;letter-spacing:2px;margin-bottom:6px;">CHARGES</div>
      <hr class="sep"/>
      <div class="section">
        ${s.totalServiceCharge > 0 ? `<div class="summary-row"><span>Service Charge:</span><span>${fmt(s.totalServiceCharge)}</span></div>` : ''}
        ${s.totalPackageFee > 0    ? `<div class="summary-row"><span>Packaging Fee:</span><span>${fmt(s.totalPackageFee)}</span></div>` : ''}
      </div>` : ''}

      <hr class="sep"/>
      <div style="text-align:center;font-size:13px;font-weight:700;letter-spacing:2px;margin-bottom:6px;">PRODUCTS OUT</div>
      <hr class="sep"/>
      <div class="section">
        <div class="items-header">
          <span style="flex:4;">Product</span>
          <span style="flex:1; text-align:right;">Qty</span>
          <span style="flex:2; text-align:right;">Amount</span>
        </div>
        ${productRows || '<div style="text-align:center;font-size:12px;padding:6px 0;">No products sold today</div>'}
      </div>

      ${footerBlock()}
    </body></html>`
  }

  // ─────────────────────────────────────────────
  // STOCK REPORT HTML
  // ─────────────────────────────────────────────
  const generateStockReportHTML = (stockData) => {
    const { items, summary } = stockData.data

    const rows = items.map(item => {
      const statusLabel = item.stockStatus === 'out_of_stock' ? 'OUT'
        : item.stockStatus === 'low_stock' ? 'LOW'
        : 'OK'
      return `
        <div class="item-row" style="margin-bottom:4px;">
          <span style="flex:3; font-size:12px; word-break:break-word; padding-right:4px;">${item.itemName}</span>
          <span style="flex:2; font-size:12px; text-align:right; white-space:nowrap;">${item.currentStock} ${item.unit}</span>
          <span style="flex:1; font-size:12px; text-align:right; font-weight:${statusLabel !== 'OK' ? '700' : '400'};">${statusLabel}</span>
        </div>`
    }).join('')

    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Stock Report</title><style>${getReceiptCSS()}</style></head><body>
      ${headerBlock('STOCK STATUS')}
      <div class="section">
        <div class="items-header">
          <span style="flex:3;">Item</span>
          <span style="flex:2; text-align:right;">Stock</span>
          <span style="flex:1; text-align:right;">Status</span>
        </div>
        ${rows || '<div style="text-align:center;font-size:12px;padding:8px 0;">No stock data</div>'}
      </div>
      <hr class="sep-solid"/>
      <div class="section">
        <div class="summary-row"><span>Total Items:</span><span>${summary.totalItems}</span></div>
        <div class="summary-row"><span>Products:</span><span>${summary.products}</span></div>
        <div class="summary-row"><span>Raw Materials:</span><span>${summary.rawMaterials}</span></div>
        <div class="summary-row"><span>Low Stock:</span><span>${summary.lowStock}</span></div>
        <div class="summary-row bold"><span>Out of Stock:</span><span>${summary.outOfStock}</span></div>
      </div>
      ${footerBlock()}
    </body></html>`
  }

  // ─────────────────────────────────────────────
  // Print via hidden iframe
  // ─────────────────────────────────────────────
  const printReport = (htmlContent) => {
    const iframe = document.createElement('iframe')
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:none;'
    document.body.appendChild(iframe)
    const doc = iframe.contentWindow.document
    doc.open()
    doc.write(htmlContent)
    doc.close()
    setTimeout(() => {
      iframe.contentWindow.focus()
      iframe.contentWindow.print()
      setTimeout(() => document.body.removeChild(iframe), 1500)
    }, 350)
  }

  // ─────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────
  const handleSalesReport = async () => {
    if (!branchId) { alert('Branch ID is required'); return }
    setLoading(true); setSelectedReport('sales')
    try {
      const data = await ReportService.getSalesReport(branchId)
      if (data.success) printReport(generateSalesReportHTML(data))
      else alert('Failed to generate sales report: ' + data.message)
    } catch (e) { alert('Error generating sales report') }
    finally { setLoading(false); setSelectedReport(null) }
  }

  const handleReturnsReport = async () => {
    if (!branchId) { alert('Branch ID is required'); return }
    setLoading(true); setSelectedReport('returns')
    try {
      const data = await ReportService.getReturnsReport(branchId)
      if (data.success) printReport(generateReturnsReportHTML(data))
      else alert('Failed to generate returns report: ' + data.message)
    } catch (e) { alert('Error generating returns report') }
    finally { setLoading(false); setSelectedReport(null) }
  }

  const handleStockReport = async () => {
    if (!branchId) { alert('Branch ID is required'); return }
    setLoading(true); setSelectedReport('stock')
    try {
      const data = await ReportService.getStockReport(branchId)
      if (data.success) printReport(generateStockReportHTML(data))
      else alert('Failed to generate stock report: ' + data.message)
    } catch (e) { alert('Error generating stock report') }
    finally { setLoading(false); setSelectedReport(null) }
  }

  const handleDailySummaryReport = async () => {
    if (!branchId) { alert('Branch ID is required'); return }
    setLoading(true); setSelectedReport('dailySummary')
    try {
      const data = await ReportService.getDailySummaryReport(branchId)
      if (data.success) printReport(generateDailySummaryReportHTML(data))
      else alert('Failed to generate daily summary: ' + data.message)
    } catch (e) { alert('Error generating daily summary report') }
    finally { setLoading(false); setSelectedReport(null) }
  }

  // ─────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────
  const reportCards = [
    {
      id: 'sales',
      label: 'Sales Report - TODAY',
      sub: 'Order #, Amount, Discount, Returns',
      icon: TrendingUp,
      color: 'orange',
      handler: handleSalesReport
    },
    {
      id: 'returns',
      label: 'Returns Report - TODAY',
      sub: 'Order #, Items, Refund Amount',
      icon: RotateCcw,
      color: 'red',
      handler: handleReturnsReport
    },
    {
      id: 'stock',
      label: 'Stock Status Report',
      sub: 'Current stock levels and status',
      icon: Package,
      color: 'green',
      handler: handleStockReport
    },
    {
      id: 'dailySummary',
      label: 'Daily Audit Summary',
      sub: 'Cash/Card split · Products out · Charges',
      icon: ClipboardList,
      color: 'blue',
      handler: handleDailySummaryReport
    }
  ]

  const colorMap = {
    orange: { border: 'border-orange-500 bg-orange-50', hover: 'hover:border-orange-300 hover:bg-orange-50', spinner: 'border-orange-600', text: 'text-orange-600', icon: 'text-orange-600' },
    red:    { border: 'border-red-500 bg-red-50',       hover: 'hover:border-red-300 hover:bg-red-50',       spinner: 'border-red-600',    text: 'text-red-600',    icon: 'text-red-600' },
    green:  { border: 'border-green-500 bg-green-50',   hover: 'hover:border-green-300 hover:bg-green-50',   spinner: 'border-green-600',  text: 'text-green-600',  icon: 'text-green-600' },
    blue:   { border: 'border-blue-500 bg-blue-50',     hover: 'hover:border-blue-300 hover:bg-blue-50',     spinner: 'border-blue-600',   text: 'text-blue-600',   icon: 'text-blue-600' }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-lg">
          <div className="flex items-center">
            <FileText className="h-6 w-6 text-blue-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">All Reports — TODAY</h2>
              <p className="text-sm text-gray-500">
                {branchInfo?.name || 'Branch'} • {new Date().toLocaleDateString('en-GB')}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Branch warning if not resolved */}
          {!branchId ? (
            <div className="mb-5 p-4 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Branch not detected</p>
                <p className="text-xs text-amber-700 mt-1">
                  Reports can only be generated for your assigned branch. Please ensure you are logged in to a branch-enabled session.
                </p>
              </div>
            </div>
          ) : (
            <div className="mb-5 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
              <FileText className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-800">
                Showing data for <strong>{branchInfo?.name || branchId}</strong>. Click a report to open the print dialog — optimised for 80mm thermal printers.
              </p>
            </div>
          )}

          {/* Report Buttons */}
          <div className="space-y-3">
            {reportCards.map(card => {
              const c = colorMap[card.color]
              const isActive = selectedReport === card.id && loading
              const Icon = card.icon
              return (
                <button
                  key={card.id}
                  onClick={card.handler}
                  disabled={loading || !branchId}
                  className={`w-full p-4 text-left border-2 rounded-lg transition-colors
                    ${isActive ? c.border : `border-gray-200 ${c.hover}`}
                    ${(loading && !isActive) || !branchId ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon className={`h-5 w-5 ${c.icon}`} />
                      <div>
                        <div className="font-medium text-gray-900">{card.label}</div>
                        <div className="text-sm text-gray-500">{card.sub}</div>
                      </div>
                    </div>
                    {isActive ? (
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 border-2 ${c.spinner} border-t-transparent rounded-full animate-spin`} />
                        <span className={`text-sm ${c.text}`}>Processing…</span>
                      </div>
                    ) : (
                      <FileText className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          {/* Footer note */}
          <div className="mt-5 p-4 bg-gray-50 border border-gray-200 rounded-lg flex items-start gap-2">
            <Calendar className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-600">
              All reports show <strong>today's data only</strong> ({new Date().toLocaleDateString('en-GB')}).
              Sales report includes any returns processed today as negative entries.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AllReportsModal